import type { LucideIcon } from 'lucide-react'
import { useQuery } from '@apollo/client/react'
import { RoleEnum } from '@zeus/index'
import { AlertTriangle, CalendarClock, CalendarPlus, ChevronRight, CircleCheckBig, Loader, UserRoundX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { GQL_BACK_OFFICE_HOME } from '~/gql/home/home'
import { formatSessionDate } from '~/helpers/dates/session-dates'
import { useAuth } from '~/helpers/store/auth'

/**
 * Nombre de parcours listés par bloc de calendrier.
 *
 * Une promo qui lance douze sessions le même lundi ne doit pas repousser tout
 * le reste sous la ligne de flottaison. Au-delà, la liste des parcours fait le
 * travail — et le lien y mène.
 */
const MAX_ROWS = 4

/** Un fait du jour : une phrase, et l'écran où l'on peut y faire quelque chose. */
type Fact = {
  key: string
  icon: LucideIcon
  /** Teinte de la pastille, qui dit l'urgence sans avoir à la lire. */
  tone: string
  title: string
  detail?: string
  to: string
}

function FactRow({ fact }: { fact: Fact }) {
  return (
    <li>
      <Link
        to={fact.to}
        className="flex items-center gap-4 rounded-lg border bg-card p-4 transition-colors hover:bg-accent"
      >
        <span className={`flex size-9 shrink-0 items-center justify-center rounded-full ${fact.tone}`}>
          <fact.icon className="size-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium">{fact.title}</span>
          {fact.detail && (
            <span className="block text-xs text-muted-foreground">{fact.detail}</span>
          )}
        </span>
        <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
      </Link>
    </li>
  )
}

/**
 * `note` se place SOUS les lignes de la section, pas en pied de page : elle les
 * commente, et lue trois sections plus bas elle n'aurait plus de référent.
 */
function Section({ title, facts, note }: { title: string, facts: Fact[], note?: string }) {
  if (facts.length === 0) return null
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</h2>
      <ul className="flex flex-col gap-2">
        {facts.map(fact => <FactRow key={fact.key} fact={fact} />)}
      </ul>
      {note && <p className="text-xs text-muted-foreground">{note}</p>}
    </section>
  )
}

/**
 * Accueil de l'admin et du formateur (#201).
 *
 * Répond à « qu'est-ce que j'ai à faire aujourd'hui ? », pas à « comment ça se
 * passe globalement » — c'est la page Statistiques qui répond à celle-là (#111,
 * #138). D'où les partis pris, qui ne sont pas cosmétiques :
 *
 *   - **des phrases, pas des chiffres posés** : chaque ligne se lit à voix
 *     haute. Un nombre seul se contemple, une phrase se traite ;
 *   - **rien qui ne mène nulle part** : toute ligne est un lien vers l'écran où
 *     l'on agit. Un fait sans destination n'a rien à faire ici ;
 *   - **le vide est un bon résultat** : une journée sans rien à faire s'affiche
 *     comme telle, sans meubler avec des indicateurs de remplissage.
 *
 * Si cet écran se met à ressembler à un tableau de bord, c'est raté.
 */
function HomePage() {
  const { t, i18n } = useTranslation()
  const { payload } = useAuth()
  const { data, previousData, loading } = useQuery(GQL_BACK_OFFICE_HOME, {
    fetchPolicy: 'cache-and-network',
  })

  const home = data?.backOfficeHome ?? previousData?.backOfficeHome
  const isAdmin = payload?.role === RoleEnum.Admin

  // Le formateur n'a accès ni aux statistiques ni aux utilisateurs : l'envoyer
  // là-bas le ferait rebondir sur une redirection. Sa liste de parcours est le
  // seul écran où il peut agir — c'est donc là que ses liens mènent.
  const sessionLink = (sessionId: string) =>
    isAdmin ? `/back-office/statistics?sessionId=${sessionId}` : '/back-office/sessions'

  // Le fuseau vient du digest et non du branding : c'est celui-là même qui a
  // découpé les « aujourd'hui » et « dans 7 jours » côté serveur, donc l'écran
  // et ses chiffres ne peuvent pas se contredire.
  const formatDate = (value: unknown) => formatSessionDate(value, i18n.language, home?.timezone)

  const attention: Fact[] = home
    ? [
        // Pas de troncature ici : le serveur a déjà borné cette liste, et en
        // reprendre une seconde ferait mentir le « et N autres » plus bas.
        ...home.overdue.map(session => ({
          key: `overdue-${session.id}`,
          icon: AlertTriangle,
          tone: 'bg-red-500/10 text-red-600 dark:text-red-400',
          title: t('backHome.overdue.title', { name: session.name, count: session.pendingLearners }),
          detail: t('backHome.overdue.detail', { date: formatDate(session.endDate) }),
          to: sessionLink(session.id),
        })),
        ...home.endingSoon
          .filter(session => session.pendingLearners > 0)
          .slice(0, MAX_ROWS)
          .map(session => ({
            key: `ending-${session.id}`,
            icon: CalendarClock,
            tone: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
            title: t('backHome.endingSoon.title', { name: session.name, date: formatDate(session.endDate) }),
            detail: t('backHome.endingSoon.detail', { count: session.pendingLearners, total: session.enrolledLearners }),
            to: sessionLink(session.id),
          })),
        // Le lien porte le filtre « jamais commencé » de la page Utilisateurs
        // (#137), qui liste exactement les gens comptés ici. Réservé à l'admin :
        // c'est le seul à pouvoir ouvrir cet écran.
        ...(isAdmin && home.neverStartedLearners > 0
          ? [{
              key: 'never-started',
              icon: UserRoundX,
              tone: 'bg-muted text-muted-foreground',
              title: t('backHome.neverStarted.title', { count: home.neverStartedLearners }),
              detail: t('backHome.neverStarted.detail', { count: home.neverStartedLearners }),
              to: '/back-office/users?progress=NOT_STARTED',
            }]
          : []),
      ]
    : []

  const upcoming: Fact[] = (home?.startingSoon ?? []).slice(0, MAX_ROWS).map(session => ({
    key: `starting-${session.id}`,
    icon: CalendarPlus,
    tone: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
    title: t('backHome.startingSoon.title', { name: session.name, date: formatDate(session.startDate) }),
    detail: t('backHome.startingSoon.detail', { count: session.enrolledLearners }),
    to: isAdmin ? sessionLink(session.id) : '/back-office/sessions',
  }))

  const today: Fact[] = (home?.completedToday ?? []).slice(0, MAX_ROWS).map(row => ({
    key: `done-${row.sessionId}-${row.moduleId}`,
    icon: CircleCheckBig,
    tone: 'bg-green-500/10 text-green-600 dark:text-green-400',
    title: t('backHome.completedToday.title', { count: row.learners, module: row.moduleTitle }),
    detail: row.sessionName,
    to: sessionLink(row.sessionId),
  }))

  const total = attention.length + upcoming.length + today.length

  // Deux écarts que les lignes de retard ne peuvent pas dire seules : plusieurs
  // parcours peuvent concerner les mêmes personnes, et la liste est tronquée
  // côté serveur alors que les compteurs, eux, portent sur la totalité. Les
  // taire laisserait le lecteur additionner les lignes et tomber sur un autre
  // nombre que le nôtre.
  const overdueNote = home && home.overdue.length > 1
    ? [
        t('backHome.overdue.learners', { count: home.overdueLearners }),
        ...(home.overdueSessions > home.overdue.length
          ? [t('backHome.overdue.more', { count: home.overdueSessions - home.overdue.length })]
          : []),
      ].join(' ')
    : undefined

  if (loading && !home) {
    return (
      <div className="flex justify-center p-10">
        <Loader className="size-6 animate-spin" />
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-8 p-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">
          {t('backHome.greeting', { name: home?.firstName ?? '' })}
        </h1>
        <p className="text-sm text-muted-foreground">
          {attention.length > 0
            ? t('backHome.subtitle.attention', { count: attention.length })
            : t('backHome.subtitle.clear')}
        </p>
      </header>

      {total === 0
        ? (
            <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed p-8">
              <p className="text-sm text-muted-foreground">{t('backHome.empty')}</p>
              <Link
                to="/back-office/sessions"
                className="text-sm font-medium underline underline-offset-4"
              >
                {t('backHome.empty.cta')}
              </Link>
            </div>
          )
        : (
            <div className="flex flex-col gap-6">
              <Section title={t('backHome.section.attention')} facts={attention} note={overdueNote} />
              <Section title={t('backHome.section.upcoming')} facts={upcoming} />
              <Section title={t('backHome.section.today')} facts={today} />
            </div>
          )}
    </div>
  )
}

export default HomePage
